import { DeviceType } from '@ixicare/resources';
import { t } from '~i18n';

/** A single parsed CSV row, ready to spread into Device.submitDevice. */
export interface DeviceCsvRow {
  macAddress?: string;
  imeiNumber?: string;
  phoneNumber?: string;
}

export interface ParsedDeviceCsv {
  rows: DeviceCsvRow[];
  errors: string[];
}

type CsvColumn = 'mac_address' | 'imei_number' | 'phone_number';

const COLUMN_TO_FIELD: Record<CsvColumn, keyof DeviceCsvRow> = {
  mac_address: 'macAddress',
  imei_number: 'imeiNumber',
  phone_number: 'phoneNumber',
};

/**
 * Columns required for each device type. Mirrors the single-add field matrix:
 * mac for everything except Eview/Twig; imei + phone only for IXI_PRO/Eview/Twig.
 */
export const requiredCsvColumns = (type: DeviceType): CsvColumn[] => {
  switch (type) {
    case DeviceType.IXI_PRO:
      return ['mac_address', 'imei_number', 'phone_number'];
    case DeviceType.EVIEW:
    case DeviceType.TWIG:
      return ['imei_number', 'phone_number'];
    case DeviceType.IXI_LITE:
    case DeviceType.IXI_BLACK:
    case DeviceType.WALL_MOUNTED_DISTRESS_BUTTON:
      return ['mac_address'];
    default:
      return [];
  }
};

/** Lightweight CSV parser — mac/imei/phone values never contain commas, so split is safe. */
export const parseDeviceCsv = (text: string, type: DeviceType): ParsedDeviceCsv => {
  const required = requiredCsvColumns(type);
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length < 2) {
    return { rows: [], errors: [t('device.form.csvEmpty')] };
  }

  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const missingColumns = required.filter((col) => !headers.includes(col));
  if (missingColumns.length > 0) {
    return { rows: [], errors: [t('device.form.csvMissingColumns', { columns: missingColumns.join(', ') })] };
  }

  const rows: DeviceCsvRow[] = [];
  const errors: string[] = [];

  lines.slice(1).forEach((line, index) => {
    const cells = line.split(',').map((c) => c.trim());
    const row: DeviceCsvRow = {};
    const missingValues: CsvColumn[] = [];

    (Object.keys(COLUMN_TO_FIELD) as CsvColumn[]).forEach((col) => {
      const headerIndex = headers.indexOf(col);
      const value = headerIndex >= 0 ? cells[headerIndex] : undefined;
      if (value) {
        row[COLUMN_TO_FIELD[col]] = value;
      } else if (required.includes(col)) {
        missingValues.push(col);
      }
    });

    if (missingValues.length > 0) {
      // +2: 1 for the header row, 1 for 1-based line numbering.
      errors.push(t('device.form.csvRowMissing', { row: index + 2, columns: missingValues.join(', ') }));
      return;
    }

    rows.push(row);
  });

  return { rows, errors };
};
